/**
 * The Velox-KAi mark — the canonical asset, inlined into the bundle.
 *
 * The artwork is `web/public/logo-mark.svg` and it is used AS IS: two nodes on
 * a navy `#1E3A5F` rounded tile, one white, one green `#22A06B`. Nothing here
 * recolours it, redraws it or reproduces it — the file is read at build time
 * and its markup is emitted verbatim, so the repo keeps exactly one copy of the
 * mark and this component cannot drift from it.
 *
 * Why inlined rather than an `<img>` pointing at the public copy of the file
 * ---------------------------------------------------------------------------
 * The report PDF is this frontend's own print route, printed by a headless
 * Chromium that the exporter starts with **no network** (`app/pdfexport.py`:
 * `--host-resolver-rules=MAP * ~NOTFOUND, EXCLUDE 127.0.0.1`) against a
 * loopback render ticket. The server behind that ticket mounts exactly ONE
 * static path — `/assets`, Vite's asset directory (`app/main.py`). A file in
 * `web/public/` is copied to the *root* of `dist/`, i.e. `dist/logo-mark.svg`,
 * which is outside that mount: `GET /logo-mark.svg` answers 404 during a
 * render, and with no network there is no fallback. The mark would print as a
 * broken image on a document a client is handed.
 *
 * Inlining puts the artwork inside `assets/index-<hash>.js` — a file the render
 * page already loads and the app already serves — so the mark needs no second
 * request, no second mount and no ordering guarantee against the print view's
 * readiness flag. Same bytes on screen, on paper and in the PDF.
 *
 * Accessibility: the wrapper is `aria-hidden`, so the mark is decorative
 * *inside the lockup* — the lockup's accessible name stays the visible text
 * ("Baueingabe-Check-KAi von Velox-KAi"), and the name "Velox-KAi" is not announced twice.
 * The canonical file keeps its own `role="img" aria-label="Velox-KAi"` for use
 * outside this app, where it stands alone and must name itself.
 */
import markSvg from '../../public/logo-mark.svg?raw'

import { cn } from '@/lib/utils'

/** The header/print lockup size from the brand spec. */
export const BRAND_MARK_SIZE = 24

export function BrandMark({ className, size = BRAND_MARK_SIZE }: { className?: string; size?: number }) {
  return (
    <span
      // Decorative here: the visible text next to it already says "Velox-KAi".
      aria-hidden="true"
      data-brand-asset="logo-mark"
      className={cn('brand-mark inline-flex shrink-0', className)}
      style={{ width: size, height: size }}
      // The canonical SVG, verbatim. Not user input and not fetched at runtime:
      // a build-time constant read from the repo's own asset file.
      dangerouslySetInnerHTML={{ __html: markSvg }}
    />
  )
}
